import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { colonyApi } from "@/lib/colonyApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Home } from "lucide-react";
import { useColonyBrand } from "@/pages/colony/useColonyBrand";

/**
 * Colony-portal "Forgot your password?" page. Stays entirely within
 * /colonies/* — no link back to the Catskills pool login — and hits
 * the colony auth endpoint so the reset email is Management USA NJ
 * branded and the reset link lands on /colonies/accept-invite.
 */
export default function ColonyForgotPassword() {
  useColonyBrand();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await colonyApi.post("/colony/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      // Backend intentionally returns ok even on unknown emails; a
      // failure here means the endpoint itself is unreachable.
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-slate-900 to-slate-950 text-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 border border-emerald-700 rounded-full px-3 py-1 text-xs tracking-widest uppercase text-emerald-300 mb-4">
            <Home className="w-3.5 h-3.5" /> Colony portal
          </div>
          <h1 className="font-display text-3xl font-black tracking-tighter" data-testid="colony-forgot-title">
            Management USA NJ
          </h1>
          <p className="text-sm text-slate-400 mt-1">Bungalow colony portal</p>
        </div>

        <div className="bg-slate-800/60 border border-slate-700 rounded-md p-6 shadow-xl">
          {sent ? (
            <div className="text-center space-y-3" data-testid="colony-forgot-sent">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-600/20 flex items-center justify-center">
                <Mail className="w-6 h-6 text-emerald-300" />
              </div>
              <h2 className="font-semibold text-lg">Check your email</h2>
              <p className="text-sm text-slate-300">
                If a colony portal account exists for <b>{email}</b>, we&apos;ve emailed a reset link.
                It&apos;s valid for 48 hours.
              </p>
              <Link
                to="/managementusa"
                className="inline-block text-sm text-emerald-300 hover:text-emerald-200 underline underline-offset-4 mt-4"
                data-testid="colony-forgot-back-login"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="colony-forgot-form">
              <div>
                <h2 className="font-semibold text-lg">Forgot your password?</h2>
                <p className="text-sm text-slate-400 mt-1">
                  Enter the email your colony contact uses and we&apos;ll send you a link to set a new one.
                </p>
              </div>
              <div>
                <Label htmlFor="cfp-email" className="text-slate-300">Email</Label>
                <Input
                  id="cfp-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="bg-slate-900 border-slate-700 text-slate-50"
                  data-testid="colony-forgot-email"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="colony-forgot-submit"
              >
                {loading ? "Sending…" : "Send reset link"}
              </Button>
              <div className="text-center text-sm">
                <Link
                  to="/managementusa"
                  className="text-slate-400 hover:text-slate-200"
                  data-testid="colony-forgot-back-login"
                >
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
